import React, { useEffect, useRef, useState } from "react";
import "./CommonDropdownInfinite.css";
import useDebounce from "../useDebounce";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

const CommonDropdownInfinite = ({
  label = "Select Items",
  fetchData,
  onChange,
  selectedItems = [],
  pageSize = 10,
  isMulti = true,
  disabled = false,
  error = false,
  errorMessage = "",
}) => {
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selected, setSelected] = useState(
    isMulti ? selectedItems : selectedItems || null
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const debouncedSearch = useDebounce(search, 500);
  const containerRef = useRef(null);

  useEffect(() => {
    setSelected(isMulti ? selectedItems : selectedItems || null);
  }, [selectedItems]);

  const loadItems = async (reset = false, query = search, nextPage = 1) => {
    if (!reset) setIsLoadingMore(true);
    const response = await fetchData(query, nextPage, pageSize);
    const newItems = response.items || [];
    const total = response.total || 0;

    const merged = reset ? newItems : [...displayedItems, ...newItems];
    setItems(reset ? newItems : [...items, ...newItems]);
    setDisplayedItems(merged);
    setPage(nextPage);
    setHasMore(merged.length < total);
    setIsLoadingMore(false);
  };

  // API call only when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      loadItems(true, debouncedSearch, 1);
    }
  }, [dropdownOpen, debouncedSearch]);

  // Handle prefill - API call on initial load if items are selected
  useEffect(() => {
    if (isInitialLoad) {
      const hasSelectedItems = isMulti 
        ? selectedItems?.length > 0 
        : selectedItems !== null && selectedItems !== undefined;
      
      if (hasSelectedItems) {
        loadItems(true, "", 1);
      }
      setIsInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    if (isMulti) {
      setSelected(selectedItems);
    } else {
      if (typeof selectedItems === "object" && selectedItems !== null) {
        setSelected(selectedItems);
      } else if (typeof selectedItems === "string") {
        const existing = items.find((i) => i._id === selectedItems);
        if (existing) setSelected(existing);
      }
    }
  }, [selectedItems, items]);

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      const nextPage = page + 1;
      loadItems(false, search, nextPage);
    }
  };

  const handleScroll = () => {
    const el = containerRef.current?.querySelector(".dropdown-list");
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      loadMore();
    }
  };

  const handleSelect = (item) => {
    if (disabled) return;
    if (isMulti) {
      const isSelected = selected.some((s) => s._id === item._id);
      const updated = isSelected
        ? selected.filter((s) => s._id !== item._id)
        : [...selected, item];
      setSelected(updated);
      onChange && onChange(updated);
    } else {
      setSelected(item);
      setDropdownOpen(false);
      onChange && onChange(item._id);
    }
  };

  const handleRemove = (_id) => {
    const updated = selected.filter((item) => item._id !== _id);
    setSelected(updated);
    onChange && onChange(updated);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getInitials = (name = "") => {
    const words = name.split(" ");
    return words.slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  };

  const isItemSelected = (item) => {
    return isMulti
      ? selected.some((s) => s._id === item._id)
      : selected === item._id || selected?._id === item._id;
  };

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", fontFamily: "sans-serif", position: "relative" }}
    >
      <div style={{ position: "relative" }}>
        <label
          style={{
            position: "absolute",
            top: dropdownOpen || (isMulti ? selected?.length > 0 : selected) ? -0 : "50%",
            left: 6,
            fontSize: dropdownOpen || (isMulti ? selected?.length > 0 : selected) ? 12 : 14,
            color: "#666",
            background: !disabled?"#fff":"#fff",
            padding: "0 4px",
            transform: "translateY(-50%)",
            transition: "all 0.2s ease",
            pointerEvents: "none",
          }}
        >
          {label}
        </label>

        <div
          style={{
            border: error ? "0px solid #e60404" : dropdownOpen ? "0.5px solid #1450ec" : "1px solid #e8e7e7",
            padding: "6px",
            borderRadius: 4,
            display: "flex",
            flexWrap: "wrap",
            transition: "all .25s",
            minHeight: 42,
            cursor: !disabled?"pointer":"",
            alignItems: "center",
            paddingTop: 6,
            paddingBottom: 6,
            boxSizing: "border-box",
            outline: error ? "0.1px solid #e60404" : dropdownOpen ? "0.5px solid #1450ec" : "1px solid #e8e7e700",
            backgroundColor: disabled ? "#light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3))" : "",
            
          }}
          onClick={() => setDropdownOpen((pre) => !pre)}
        
        >
          {isMulti ? (
            selected?.length === 0 ? (
              <span style={{ color: "#888", marginLeft: 4, fontSize: "14px" }}>
                {label}
              </span>
            ) : selected?.length > 10 ? (
              <div
                style={{
                  background: "#eee",
                  borderRadius: 20,
                  padding: "2px 8px",
                  margin: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {selected?.length} selected
                <span
                  style={{ marginLeft: 6, cursor: "pointer", fontWeight: "bold" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected([]);
                    onChange && onChange([]);
                  }}
                >
                  ×
                </span>
              </div>
            ) : (
              selected?.map((item) => (
                <div key={item._id} className="__selectedMulti_fx">
                  {item.name}
                  <span
                    className="__cancelMult_sel"
                    onClick={(e) => {
                      if (disabled) return;
                      e.stopPropagation();
                      handleRemove(item._id);
                    }}
                  >
                    ×
                  </span>
                </div>
              ))
            )
          ) : (
            <span style={{ fontSize: 14, color: selected ? "#000" : "#888" }}>
              {selected?.name || label}
            </span>
          )}
          <span style={{ marginLeft: "auto", fontSize: "16px" }}>
            {dropdownOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </span>
        </div>
      </div>

      {dropdownOpen && !disabled && (
        <div
          className="dropdown-list"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: "auto",
            border: "1px solid rgb(0 80 230)",
            borderRadius: "4px",
            background: "#fff",
            zIndex: 1000,
          }}
          onScroll={handleScroll}
        >
          <div className="__searchIn_multiBx">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="__otr_checkBx_fl">
            {displayedItems.map((item) => {
              const selectedFlag = isItemSelected(item);
              return (
                <div
                  key={item._id}
                  className="__inrChk_bx"
                  onClick={() => handleSelect(item)}
                  style={{
                    backgroundColor: !isMulti && selectedFlag ? "#e0edff" : "transparent",
                    borderRadius: 4,
                    padding: "6px 8px",
                    margin: "2px 4px",
                    cursor: "pointer",
                  }}
                >
                  {isMulti && selectedFlag && (
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => handleSelect(item)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginRight: 10 }}
                    />
                  )}
                  {isMulti
                    ? !selectedFlag && (
                        <div
                          className="custom-checkbox"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(item);
                          }}
                        >
                          {getInitials(item.name)}
                        </div>
                      )
                    : (
                      <div
                        className="custom-checkbox"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(item);
                        }}
                      >
                        {getInitials(item.name)}
                      </div>
                    )}
                  <label>
                    <span style={{ fontWeight: !isMulti && selectedFlag ? "bold" : "normal" }}>{item.name}</span>
                  </label>
                </div>
              );
            })}
          </div>

          {isLoadingMore && (
            <div
              style={{
                padding: 8,
                textAlign: "center",
                fontSize: 12,
                color: "#1450ec",
              }}
            >
              Loading...
            </div>
          )}

          {!hasMore && displayedItems?.length > 0 && !isLoadingMore && (
            <div
              style={{
                padding: 8,
                textAlign: "center",
                fontSize: 12,
                color: "#888",
              }}
            >
              No more items
            </div>
          )}

          {displayedItems?.length == 0 && (
            <div
              style={{
                padding: 8,
                textAlign: "center",
                fontSize: 12,
                color: "#888",
              }}
            >
              No Data
            </div>
          )}
        </div>
      )}
      {error && errorMessage && (
        <div className="__cdi_error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default CommonDropdownInfinite;

